import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Settings, Save, Globe, Loader2 } from "lucide-react";
import { AutosaveStatus } from "@/components/common/AutosaveStatus";
import { useTranslation } from "@/hooks/useTranslation";
import type { Event } from "@/services/api/events";
import { updateEvent, resetEventLanguage } from "@/services/api/events";
import { EventTypeSelector } from "@/components/customer/events/EventTypeSelector";
import { BabyNamesSection, CoupleSection, EventDetailsSection, WeddingScheduleSection } from "./sections";
import { toast } from "sonner";
import { useState, useEffect } from "react";
import {
  ParentsGodparentsAccordion,
  CivilCeremonyAccordion,
  ReligiousCeremonyAccordion,
  BaptismCeremonyAccordion,
  PartyCeremonyAccordion,
} from "./accordions";
import { useGeneralForm } from "./hooks";

interface FamilyData {
  brideParent1: string;
  brideParent2: string;
  groomParent1: string;
  groomParent2: string;
  godparent1: string;
  godparent2: string;
  brideParentsEnabled: boolean;
  groomParentsEnabled: boolean;
  godparentsEnabled: boolean;
}

interface GeneralTabProps {
  event: Event;
  onEventUpdated?: () => void;
  /** JUB-152: autosaved event from the server, applied without a reload. */
  onEventPatched?: (event: Event) => void;
}

export function GeneralTab({ event, onEventUpdated, onEventPatched }: GeneralTabProps) {
  const { t } = useTranslation();
  const [eventLanguage, setEventLanguage] = useState<'ro' | 'en'>(event.defaultLanguage || 'ro');
  const [resettingLanguage, setResettingLanguage] = useState(false);
  const {
    form,
    seeding,
    waitingForSave,
    landingLoadFailed,
    retryLanding,
    ceremonies,
    family,
    setCeremonies,
    setFamily,
    handleSaveNow,
    flushAll,
    retryAll,
    flushEventOnNextChange,
    flushLandingOnNextChange,
    autosaveStatus,
    restoredDraft,
    discardDrafts,
    fieldErrors,
  } = useGeneralForm({ event, onEventUpdated, onEventPatched });

  // Update eventLanguage when event changes
  useEffect(() => {
    setEventLanguage(event.defaultLanguage || 'ro');
  }, [event.defaultLanguage]);

  const {
    formState: { errors },
    watch,
  } = form;

  const eventName = watch("event_name");
  const eventDate = watch("event_date");
  const includesWedding = watch("event_includes_wedding") ?? true;
  const includesBaptism = watch("event_includes_baptism") ?? false;
  const babyCount = watch("event_baby_count");

  // Switches and selects save right away; typed text is debounced (JUB-152).
  const setEventToggle = (...args: Parameters<typeof form.setValue>) => {
    flushEventOnNextChange();
    form.setValue(...args);
  };

  // Update ceremony handlers
  const updateCeremony = (
    type: "civil" | "religious" | "party" | "baptism",
    updates: Partial<typeof ceremonies.civil>
  ) => {
    if ("enabled" in updates) flushLandingOnNextChange();
    setCeremonies((prev) => ({
      ...prev,
      [type]: { ...prev[type], ...updates },
    }));
  };

  // Update family handlers
  const updateFamily = (updates: Partial<FamilyData>) => {
    if (Object.keys(updates).some((key) => key.endsWith("Enabled"))) flushLandingOnNextChange();
    setFamily((prev) => ({ ...prev, ...updates }));
  };

  const handleLanguageChange = async (newLanguage: 'ro' | 'en') => {
    try {
      await updateEvent(event.uuid, { defaultLanguage: newLanguage });
      setEventLanguage(newLanguage);
      toast.success(t('common.languageUpdated'));
      if (onEventUpdated) {
        onEventUpdated();
      }
    } catch (error: unknown) {
      console.error('Failed to update event language:', error);
      toast.error(t('common.failedToUpdateLanguage'));
    }
  };

  const handleResetLanguage = async () => {
    try {
      setResettingLanguage(true);
      const updated = await resetEventLanguage(event.uuid);
      setEventLanguage(updated.defaultLanguage || 'ro');
      toast.success(t('common.languageReset'));
      if (onEventUpdated) {
        onEventUpdated();
      }
    } catch (error: unknown) {
      console.error('Failed to reset event language:', error);
      toast.error(t('common.failedToResetLanguage'));
    } finally {
      setResettingLanguage(false);
    }
  };

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        void handleSaveNow();
      }}
      // Leaving a field is a natural save point (name/date/couple save only then).
      onBlur={() => void flushAll()}
      className="space-y-6"
      data-testid="general-form"
    >
      {/* Outside the fieldset so it stays clickable while the form is locked. */}
      {landingLoadFailed && (
        <div
          role="alert"
          className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-destructive/40 bg-destructive/5 px-4 py-3 text-sm text-destructive"
          data-testid="general-load-failed"
        >
          <span>{t("autosave.loadFailed")}</span>
          <Button type="button" variant="outline" size="sm" onClick={retryLanding}>
            {t("autosave.retry")}
          </Button>
        </div>
      )}
      {/* JUB-152: locked until the saved values are on screen, so an early edit is never overwritten by the load. */}
      <fieldset
        disabled={seeding}
        aria-busy={seeding && !landingLoadFailed}
        className="space-y-6 min-w-0"
        data-testid="general-fieldset"
      >
      {/* Main General Information Card */}
      <Card className="border-2 shadow-lg">
        <CardHeader className="bg-gradient-to-r from-pink-50 to-rose-50 dark:from-pink-950/20 dark:to-rose-950/20 border-b">
          <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center space-x-3">
            <Settings className="h-6 w-6 text-pink-600 dark:text-pink-400" />
            <div>
              <CardTitle className="text-2xl">
                {t("events.form.generalInfo")}
              </CardTitle>
              <CardDescription className="mt-1">
                {t("events.form.generalInfoDescription")}
              </CardDescription>
            </div>
          </div>
          {seeding ? (
            !landingLoadFailed && (
              <span className="inline-flex items-center gap-2 text-sm text-muted-foreground" role="status" data-testid="general-loading">
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                {t(waitingForSave ? "autosave.waitingForSave" : "autosave.loadingSaved")}
              </span>
            )
          ) : (
            <AutosaveStatus
              status={autosaveStatus}
              onRetry={() => void retryAll()}
              restored={restoredDraft}
              onDiscard={discardDrafts}
            />
          )}
          </div>
        </CardHeader>
        <CardContent className="pt-6">
          <div className="space-y-6">
            <EventTypeSelector
              includesWedding={includesWedding}
              includesBaptism={includesBaptism}
              babyCount={babyCount === 1 || babyCount === 2 ? babyCount : null}
              onIncludesWeddingChange={(checked) => setEventToggle("event_includes_wedding", checked)}
              onIncludesBaptismChange={(checked) => {
                setEventToggle("event_includes_baptism", checked);
                if (!checked) form.setValue("event_baby_count", null);
                else if (!babyCount) form.setValue("event_baby_count", 1);
              }}
              onBabyCountChange={(count) => setEventToggle("event_baby_count", count)}
            />
            {includesBaptism && (
              <BabyNamesSection
                babyCount={babyCount === 1 || babyCount === 2 ? babyCount : 1}
                babyName={watch("event_star_baptism") || ""}
                babyName2={watch("event_star_baptism_2") || ""}
                onBabyNameChange={(value) => form.setValue("event_star_baptism", value)}
                onBabyName2Change={(value) => form.setValue("event_star_baptism_2", value)}
              />
            )}
            <CoupleSection
              brideName={watch("event_star_bride") || ""}
              groomName={watch("event_star_groom") || ""}
              onBrideNameChange={(value) => form.setValue("event_star_bride", value)}
              onGroomNameChange={(value) => form.setValue("event_star_groom", value)}
              isBaptismOnly={includesBaptism && !includesWedding}
            />

            <EventDetailsSection
              eventName={eventName || ""}
              eventDate={eventDate || ""}
              onEventNameChange={(value) => form.setValue("event_name", value)}
              onEventDateChange={(value) => form.setValue("event_date", value)}
              eventNameError={fieldErrors.name ?? errors.event_name?.message}
              eventDateError={fieldErrors.date ?? errors.event_date?.message}
            />

            <WeddingScheduleSection
              civilEnabled={ceremonies.civil.enabled}
              religiousEnabled={ceremonies.religious.enabled}
              partyEnabled={ceremonies.party.enabled}
              onCivilChange={(enabled) => updateCeremony("civil", { enabled })}
              onReligiousChange={(enabled) => updateCeremony("religious", { enabled })}
              onPartyChange={(enabled) => updateCeremony("party", { enabled })}
              isBaptismOnly={includesBaptism && !includesWedding}
              showBaptismSlot={(includesBaptism && includesWedding) || ceremonies.baptism.enabled}
              baptismEnabled={ceremonies.baptism.enabled}
              onBaptismChange={(enabled) => updateCeremony("baptism", { enabled })}
            />

            {/* Event Language Section */}
            <div className="space-y-3 pt-4 border-t">
              <div className="flex items-center gap-2">
                <Globe className="h-5 w-5 text-pink-600" />
                <Label className="text-base font-semibold">
                  {t('events.form.defaultLanguage')}
                </Label>
              </div>
              <p className="text-sm text-muted-foreground">
                {t('events.form.defaultLanguageDescription')}
              </p>
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
                <Select
                  value={eventLanguage}
                  onValueChange={(value) => handleLanguageChange(value as 'ro' | 'en')}
                >
                  <SelectTrigger className="h-10 w-full sm:w-48">
                    <SelectValue>{eventLanguage === "en" ? "English" : "Română"}</SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ro">Română</SelectItem>
                    <SelectItem value="en">English</SelectItem>
                  </SelectContent>
                </Select>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-10 w-full whitespace-nowrap sm:w-auto"
                  onClick={handleResetLanguage}
                  disabled={resettingLanguage}
                >
                  {resettingLanguage ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {t('common.loading')}
                    </>
                  ) : (
                    t('events.form.resetToMyDefault')
                  )}
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Accordion Sections - At same level as General Information Card */}
      <div className="space-y-4">
        <ParentsGodparentsAccordion
          brideParent1={family.brideParent1}
          brideParent2={family.brideParent2}
          groomParent1={family.groomParent1}
          groomParent2={family.groomParent2}
          godparent1={family.godparent1}
          godparent2={family.godparent2}
          brideParentsEnabled={family.brideParentsEnabled}
          groomParentsEnabled={family.groomParentsEnabled}
          godparentsEnabled={family.godparentsEnabled}
          includesBaptism={includesBaptism}
          onBrideParent1Change={(value) => updateFamily({ brideParent1: value })}
          onBrideParent2Change={(value) => updateFamily({ brideParent2: value })}
          onGroomParent1Change={(value) => updateFamily({ groomParent1: value })}
          onGroomParent2Change={(value) => updateFamily({ groomParent2: value })}
          onGodparent1Change={(value) => updateFamily({ godparent1: value })}
          onGodparent2Change={(value) => updateFamily({ godparent2: value })}
          onBrideParentsEnabledChange={(enabled) => updateFamily({ brideParentsEnabled: enabled })}
          onGroomParentsEnabledChange={(enabled) => updateFamily({ groomParentsEnabled: enabled })}
          onGodparentsEnabledChange={(enabled) => updateFamily({ godparentsEnabled: enabled })}
        />

        {ceremonies.civil.enabled && !(includesBaptism && !includesWedding) && (
          <CivilCeremonyAccordion
            enabled={ceremonies.civil.enabled}
            date={ceremonies.civil.date}
            time={ceremonies.civil.time}
            locationName={ceremonies.civil.locationName}
            address={ceremonies.civil.address}
            onDateChange={(value) => updateCeremony("civil", { date: value })}
            onTimeChange={(value) => updateCeremony("civil", { time: value })}
            onLocationNameChange={(value) => updateCeremony("civil", { locationName: value })}
            onAddressChange={(value) => updateCeremony("civil", { address: value })}
          />
        )}

        {ceremonies.religious.enabled && (includesBaptism && !includesWedding ? (
          <BaptismCeremonyAccordion
            enabled={ceremonies.religious.enabled}
            date={ceremonies.religious.date}
            time={ceremonies.religious.time}
            locationName={ceremonies.religious.locationName}
            address={ceremonies.religious.address}
            onDateChange={(value) => updateCeremony("religious", { date: value })}
            onTimeChange={(value) => updateCeremony("religious", { time: value })}
            onLocationNameChange={(value) => updateCeremony("religious", { locationName: value })}
            onAddressChange={(value) => updateCeremony("religious", { address: value })}
          />
        ) : (
          <ReligiousCeremonyAccordion
            enabled={ceremonies.religious.enabled}
            date={ceremonies.religious.date}
            time={ceremonies.religious.time}
            locationName={ceremonies.religious.locationName}
            address={ceremonies.religious.address}
            onDateChange={(value) => updateCeremony("religious", { date: value })}
            onTimeChange={(value) => updateCeremony("religious", { time: value })}
            onLocationNameChange={(value) => updateCeremony("religious", { locationName: value })}
            onAddressChange={(value) => updateCeremony("religious", { address: value })}
          />
        ))}

        {/* JUB-154: baptism held separately from the religious wedding */}
        {ceremonies.baptism.enabled && (
          <BaptismCeremonyAccordion
            title={t("events.form.ceremony.baptism")}
            enabled={ceremonies.baptism.enabled}
            date={ceremonies.baptism.date}
            time={ceremonies.baptism.time}
            locationName={ceremonies.baptism.locationName}
            address={ceremonies.baptism.address}
            onDateChange={(value) => updateCeremony("baptism", { date: value })}
            onTimeChange={(value) => updateCeremony("baptism", { time: value })}
            onLocationNameChange={(value) => updateCeremony("baptism", { locationName: value })}
            onAddressChange={(value) => updateCeremony("baptism", { address: value })}
          />
        )}

        {ceremonies.party.enabled && (
          <PartyCeremonyAccordion
            enabled={ceremonies.party.enabled}
            date={ceremonies.party.date}
            time={ceremonies.party.time}
            locationName={ceremonies.party.locationName}
            address={ceremonies.party.address}
            onDateChange={(value) => updateCeremony("party", { date: value })}
            onTimeChange={(value) => updateCeremony("party", { time: value })}
            onLocationNameChange={(value) => updateCeremony("party", { locationName: value })}
            onAddressChange={(value) => updateCeremony("party", { address: value })}
          />
        )}

        {(["civil", "religious", "party"] as const).map((type) =>
          fieldErrors[`ceremonies.${type}.datetime`] ? (
            <p key={type} className="text-sm text-destructive" data-testid={`ceremony-${type}-datetime-error`}>
              {fieldErrors[`ceremonies.${type}.datetime`]}
            </p>
          ) : null,
        )}
      </div>
      </fieldset>

      {/* Changes save as you type (JUB-152); "Save now" stays as a secondary action. */}
      <div className="flex justify-end space-x-2 pt-4">
        <Button type="submit" variant="outline" disabled={seeding || autosaveStatus === "saving"}>
          <Save className="mr-2 h-4 w-4" />
          {t("autosave.saveNow")}
        </Button>
      </div>
    </form>
  );
}

// Re-export type for backward compatibility
export type { EventFormData } from "./hooks/useGeneralForm";

